import Seo from "../components/Seo";
import PageHero from "../components/site/PageHero";
import { useReveal } from "../lib/useReveal";
import { Scale, FileText, ShieldCheck, Landmark, FileDown } from "lucide-react";

const PRINCIPLES = [
  {
    icon: Scale,
    title: "Charitable purposes",
    text: "Everything we do serves our stated purpose: the prevention and relief of poverty, hardship and suffering through humanitarian assistance, grants, essential goods, services and collaboration.",
  },
  {
    icon: ShieldCheck,
    title: "Trustee oversight",
    text: "Our trustees are responsible for the charity's direction, its safeguarding principles, and for ensuring resources are used responsibly and solely for charitable purposes.",
  },
  {
    icon: Landmark,
    title: "Financial accountability",
    text: "Donations are received through our official JustGiving page, with funds transferred through JustGiving's verified payment procedures. We never handle donors' card details on this website.",
  },
  {
    icon: FileText,
    title: "Conflicts of interest",
    text: "Trustees declare and manage conflicts of interest, and decisions affecting the charity's resources are taken collectively and recorded.",
  },
];

const DOCUMENTS = [
  { name: "Constitution", note: "Governing document" },
  { name: "Annual Report", note: "First report covering our initial period" },
  { name: "Annual Accounts", note: "Independently reviewed accounts" },
  { name: "Safeguarding Policy", note: "Protecting the people we serve" },
];

export default function Governance() {
  const ref = useReveal();
  return (
    <div ref={ref}>
      <Seo
        title="Governance & Transparency"
        path="/governance"
        description="How Mount Moriah Relief is governed: charitable purposes, trustee oversight, financial accountability, safeguarding principles and published reports."
      />
      <PageHero eyebrow="Governance &amp; Transparency" title="Accountability is part of our mission">
        <p>
          Donors, partners and the public deserve to know how a charity is run. This page explains
          our governance arrangements and will host our approved reports and documents.
        </p>
      </PageHero>

      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid gap-8 md:grid-cols-2">
            {PRINCIPLES.map((p) => (
              <article key={p.title} className="reveal bg-white border border-sandstone p-8 lg:p-10">
                <p.icon size={28} strokeWidth={1.5} className="text-gold" aria-hidden="true" />
                <h2 className="mt-6 font-serif text-2xl font-semibold text-charcoal">{p.title}</h2>
                <p className="mt-3 text-sm text-charcoal/75 leading-relaxed">{p.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-sandstone/50" aria-labelledby="documents-heading">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-gold mb-4">Documents</p>
          <h2 id="documents-heading" className="reveal font-serif text-3xl sm:text-4xl font-medium text-forest tracking-tight leading-snug max-w-2xl">
            Reports &amp; official documents
          </h2>
          <p className="reveal mt-5 max-w-2xl text-charcoal/80 leading-relaxed">
            Approved documents will be published here for download. We never publish unsigned or
            unapproved documents as final versions — each item below will become available once
            formally adopted by our trustees.
          </p>
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {DOCUMENTS.map((d) => (
              <div key={d.name} className="reveal bg-white border border-sandstone p-7 flex items-start gap-4" data-testid={`document-${d.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <FileDown size={22} strokeWidth={1.5} className="text-sage mt-1 shrink-0" aria-hidden="true" />
                <div>
                  <h3 className="font-serif text-xl font-semibold text-charcoal">{d.name}</h3>
                  <p className="mt-1 text-sm text-charcoal/70">{d.note}</p>
                  <p className="mt-3 text-xs font-medium text-gold-dark uppercase tracking-wider">
                    Pending trustee approval — awaiting publication
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
